import { useState } from 'react';
import type { WebFrequency } from '@/mocks/superAdminFrequencies';

interface FrequenciesTableProps {
  frequencies: WebFrequency[];
  onOpenAction: (f: WebFrequency) => void;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
}

const ITEMS_PER_PAGE = 20;

function statusBadge(freq: WebFrequency) {
  switch (freq.status) {
    case 'asignada':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-[10px] font-medium whitespace-nowrap">
          <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>
          Asignada
        </span>
      );
    case 'reservada':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-medium whitespace-nowrap">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
          Reservada
        </span>
      );
    case 'bloqueada':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-600 text-[10px] font-medium whitespace-nowrap">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
          Bloqueada
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-medium whitespace-nowrap">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
          Libre
        </span>
      );
  }
}

function SkeletonRow() {
  return (
    <tr className="border-b border-background-200/60 animate-pulse">
      <td className="px-4 py-3"><div className="h-4 w-20 bg-background-200 rounded"></div></td>
      <td className="px-4 py-3"><div className="h-4 w-16 bg-background-200 rounded-full"></div></td>
      <td className="px-4 py-3"><div className="h-4 w-28 bg-background-200 rounded"></div></td>
      <td className="px-4 py-3"><div className="h-8 w-8 bg-background-200 rounded-lg"></div></td>
    </tr>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-4 animate-pulse">
      <div className="flex items-start justify-between mb-3">
        <div className="space-y-1.5">
          <div className="h-5 w-24 bg-background-200 rounded"></div>
          <div className="h-3 w-16 bg-background-200 rounded-full"></div>
        </div>
        <div className="h-8 w-8 bg-background-200 rounded-lg"></div>
      </div>
      <div className="h-4 w-32 bg-background-200 rounded"></div>
    </div>
  );
}

export default function FrequenciesTable({
  frequencies,
  onOpenAction,
  loading = false,
  error = false,
  onRetry,
}: FrequenciesTableProps) {
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(frequencies.length / ITEMS_PER_PAGE));
  const safePage = Math.min(page, totalPages - 1);
  const paginated = frequencies.slice(safePage * ITEMS_PER_PAGE, (safePage + 1) * ITEMS_PER_PAGE);
  const total = frequencies.length;

  if (error) {
    return (
      <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center">
          <i className="ri-error-warning-line text-red-400 text-2xl"></i>
        </div>
        <p className="text-foreground-700 text-sm font-semibold">Error cargando frecuencias</p>
        <p className="text-foreground-400 text-xs mt-1 mb-4">No se pudieron obtener los datos del sistema.</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line text-xs"></i>
            Reintentar
          </button>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <>
        <div className="hidden lg:block bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-background-200 bg-background-100/50">
                <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Frecuencia</th>
                <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Estado</th>
                <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Radio</th>
                <th className="w-12 px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 8 }).map((_, i) => (
                <SkeletonRow key={i} />
              ))}
            </tbody>
          </table>
        </div>
        <div className="lg:hidden space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </>
    );
  }

  if (frequencies.length === 0) {
    return (
      <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
          <i className="ri-radar-line text-foreground-300 text-2xl"></i>
        </div>
        <p className="text-foreground-400 text-sm font-medium">No hay frecuencias con este filtro.</p>
        <p className="text-foreground-300 text-xs mt-1">Probá cambiando los filtros o la búsqueda.</p>
      </div>
    );
  }

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    return (
      <div className="flex items-center justify-between pt-4 px-4">
        <p className="text-foreground-400 text-[10px]">
          {safePage * ITEMS_PER_PAGE + 1}–{Math.min((safePage + 1) * ITEMS_PER_PAGE, total)} de {total.toLocaleString()}
        </p>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage(Math.max(0, safePage - 1))}
            disabled={safePage === 0}
            className={`w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-colors ${
              safePage === 0
                ? 'bg-background-100 text-foreground-300 cursor-not-allowed'
                : 'bg-background-100 text-foreground-600 hover:bg-background-200'
            }`}
          >
            <i className="ri-arrow-left-s-line text-sm"></i>
          </button>
          {Array.from({ length: Math.min(totalPages, 7) }).map((_, i) => {
            let pageNum: number;
            if (totalPages <= 7) {
              pageNum = i;
            } else if (safePage < 4) {
              pageNum = i;
            } else if (safePage > totalPages - 4) {
              pageNum = totalPages - 7 + i;
            } else {
              pageNum = safePage - 3 + i;
            }
            return (
              <button
                key={pageNum}
                onClick={() => setPage(pageNum)}
                className={`w-8 h-8 rounded-lg text-xs font-medium cursor-pointer transition-colors ${
                  pageNum === safePage
                    ? 'bg-foreground-950 text-background-50'
                    : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                }`}
              >
                {pageNum + 1}
              </button>
            );
          })}
          <button
            onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))}
            disabled={safePage === totalPages - 1}
            className={`w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-colors ${
              safePage === totalPages - 1
                ? 'bg-background-100 text-foreground-300 cursor-not-allowed'
                : 'bg-background-100 text-foreground-600 hover:bg-background-200'
            }`}
          >
            <i className="ri-arrow-right-s-line text-sm"></i>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
      {/* Desktop table */}
      <div className="hidden lg:block">
        <table className="w-full">
          <thead>
            <tr className="border-b border-background-200 bg-background-100/50">
              <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Frecuencia</th>
              <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Estado</th>
              <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Radio</th>
              <th className="w-12 px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((freq, idx) => (
              <tr
                key={freq.id}
                onClick={() => onOpenAction(freq)}
                className={`border-b border-background-200/60 hover:bg-background-50 transition-colors cursor-pointer ${
                  freq.status === 'bloqueada' ? 'bg-red-50/20' : ''
                }`}
                style={{ animation: `fadeInUp 0.15s ease-out ${idx * 0.012}s both` }}
              >
                <td className="px-4 py-3">
                  <span className={`font-mono text-xs font-semibold ${
                    freq.status === 'bloqueada' ? 'text-red-500 line-through decoration-red-300' : 'text-foreground-800'
                  }`}>
                    {freq.frequency}
                  </span>
                </td>
                <td className="px-4 py-3">{statusBadge(freq)}</td>
                <td className="px-4 py-3">
                  {freq.assignedRadio ? (
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-background-100 flex items-center justify-center shrink-0">
                        <i className="ri-radio-line text-foreground-500 text-[10px]"></i>
                      </div>
                      <span className="text-foreground-800 text-xs font-medium truncate max-w-[180px]">{freq.assignedRadio}</span>
                    </div>
                  ) : freq.reservedFor ? (
                    <span className="text-foreground-500 text-xs flex items-center gap-1">
                      <i className="ri-bookmark-line text-amber-500 text-[10px]"></i>
                      {freq.reservedFor}
                    </span>
                  ) : freq.status === 'bloqueada' ? (
                    <span className="text-foreground-400 text-[10px] flex items-center gap-1">
                      <i className="ri-forbid-line text-red-400 text-[9px]"></i>
                      {freq.blockedReason || 'Bloqueada'}
                    </span>
                  ) : (
                    <span className="text-foreground-300 text-xs">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={(e) => { e.stopPropagation(); onOpenAction(freq); }}
                    className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                    title="Acciones"
                  >
                    <i className="ri-more-2-fill text-foreground-500 text-sm"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {renderPagination()}
      </div>

      {/* Mobile cards */}
      <div className="lg:hidden p-4 space-y-2">
        {paginated.map((freq, idx) => (
          <div
            key={freq.id}
            onClick={() => onOpenAction(freq)}
            className={`bg-background-50 border rounded-2xl p-4 cursor-pointer transition-colors hover:border-foreground-200 ${
              freq.status === 'bloqueada' ? 'border-red-200 bg-red-50/20' : 'border-background-200'
            }`}
            style={{ animation: `fadeInUp 0.15s ease-out ${idx * 0.012}s both` }}
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <span className={`font-mono text-sm font-bold ${
                  freq.status === 'bloqueada' ? 'text-red-500 line-through' : 'text-foreground-800'
                }`}>
                  {freq.frequency}
                </span>
                <div className="mt-1">{statusBadge(freq)}</div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); onOpenAction(freq); }}
                className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors shrink-0"
              >
                <i className="ri-more-2-fill text-foreground-500 text-sm"></i>
              </button>
            </div>
            <div className="flex items-center gap-2">
              {freq.assignedRadio ? (
                <>
                  <i className="ri-radio-line text-foreground-400 text-[10px]"></i>
                  <span className="text-foreground-600 text-[11px]">{freq.assignedRadio}</span>
                </>
              ) : freq.reservedFor ? (
                <>
                  <i className="ri-bookmark-line text-amber-500 text-[10px]"></i>
                  <span className="text-foreground-500 text-[11px]">{freq.reservedFor}</span>
                </>
              ) : freq.status === 'bloqueada' ? (
                <span className="text-foreground-400 text-[10px] flex items-center gap-1">
                  <i className="ri-forbid-line text-red-400 text-[9px]"></i>
                  {freq.blockedReason || 'Bloqueada'}
                </span>
              ) : (
                <span className="text-foreground-300 text-[10px]">Sin asignar</span>
              )}
            </div>
          </div>
        ))}
        {renderPagination()}
      </div>
    </div>
  );
}